import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { STATS, COMPANY, CLIENTS } from "../lib/data";

const VALUES = [
  { t: "Experience", d: "Years of industry knowledge across banking, insurance and retail fit-outs." },
  { t: "Single Point of Contact", d: "One contract for design, civil, carpentry, MEP, networking and finishing." },
  { t: "Trusted Vendor Network", d: "Long-standing associations to keep timelines and costs predictable." },
  { t: "Quality Workmanship", d: "In-house team committed to clean execution and snag-free handover." },
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About Alex Interiors"
        title={<span className="text-yellow-400">A turnkey interior contractor from Kolkata, executing nationwide.</span>}
        sub={`Established in ${COMPANY.founded} — delivering commercial interiors for banks, insurers and leading brands across India.`}
        image="https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=2400&q=85"
      />

      {/* Story */}
      <section className="bg-[#FAFAFA] py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Our story</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-6 leading-tight">
              Built on banking floors. Trusted by brands.
            </h2>
          </div>
          <div className="lg:col-span-7 text-lg text-[#475569] leading-relaxed space-y-6">
            <p>
              Headquartered in Kolkata and operating across India since {COMPANY.founded}, Alex Interiors is a turnkey interior contractor serving the country's leading banks, insurers, financial institutions and retail brands.
            </p>
            <p>
              We bring design, civil, carpentry, MEP, networking and life-safety systems together under a single contract — delivering predictable timelines, audit-grade documentation and consistent quality across every site we execute.
            </p>
            <p>
              Our ambition is straightforward: to advance our clients' business through disciplined planning, considered design and uncompromising workmanship — at one branch or across a nationwide rollout.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s) => (
            <div key={s.label} className="py-12 px-4 text-center">
              <p className="font-heading text-4xl sm:text-5xl font-bold text-[#C5A059]">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-white/60 mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-24 lg:py-28 border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <p className="eyebrow">What sets us apart</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">Four reasons clients return.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {VALUES.map((v, i) => (
              <div key={v.t} className="bg-white p-8">
                <span className="text-xs text-[#C5A059] tracking-widest">0{i + 1}</span>
                <h3 className="font-heading text-xl font-semibold mt-3">{v.t}</h3>
                <p className="text-[#475569] mt-3 text-sm leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-[#FAFAFA] py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">Our promise</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4 leading-tight">
              One contract. One accountable team. Zero hand-offs.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2E8F0] p-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Our aim</p>
              <h3 className="font-heading text-xl font-semibold mt-3 leading-snug">
                Improve our client's business through strategic innovation, creativity and quality workmanship.
              </h3>
            </div>
            <div className="bg-white border border-[#E2E8F0] p-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Our model</p>
              <h3 className="font-heading text-xl font-semibold mt-3 leading-snug">
                A single, turnkey partner — owning the outcome from drawing board to handover.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Clients strip */}
      <section className="bg-white py-20 border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
            {CLIENTS.map((c) => (
              <div key={c.name} className="flex items-center justify-center">
                <img
                  src={c.logo}
                  alt={c.name}
                  className="logo-greyscale h-10 max-w-[140px] object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A192F] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h3 className="font-heading text-3xl sm:text-4xl font-bold">Have a project? Let's talk.</h3>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0A192F] px-8 py-4 text-sm font-semibold hover:bg-white transition-colors"
          >
            Get in Touch <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
